import { Component, inject, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { differenceInDays } from 'date-fns';
import { DriverOverviewService } from '../../../../../_shared/services';
import { GlobalDriverOverviewService } from '../../../services/global-driver-overview.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { AccountDateFilterRequest } from '../../../../../_shared/models/account-date-filter-request.model';
import { AccountFilterData, DateIntervalFilterData } from '../../../../../_shared/models';
import { isEmptyObject } from '../../../../../_shared/helpers/object.helper';
import { TimeSeriesGroupingHelper } from '../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { MultiLineChartComponent, MultiLineChartDataPoint, MultiLineChartSeries, LegendItem } from '../../../../../_shared/components/graphs/multi-line-chart/multi-line-chart.component';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { AccountFilterComponent } from '../../../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../../../_shared/components/filters/date-filter/date-filter.component';
import { GraphErrorStateComponent } from '../../../../../_shared/components/graphs/graph-error-state/graph-error-state.component';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-driver-performance-modal',
  standalone: true,
  imports: [
    MultiLineChartComponent,
    GraphTrendIconComponent,
    EmptyStateComponent,
    AccountFilterComponent,
    DateFilterComponent,
    GraphErrorStateComponent,
    TranslocoPipe
],
  templateUrl: './performance-modal.component.html',
  styleUrl: './performance-modal.component.scss',
})
export class PerformanceModalComponent implements OnDestroy {
  private activeModal = inject(NgbActiveModal);
  private driverOverviewService = inject(DriverOverviewService);
  private globalDriverOverviewService = inject(GlobalDriverOverviewService);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);
  private cancelSubject: Subject<void> = new Subject<void>();

  isLoading: boolean = true;
  hasError: boolean = false;
  totalValue: number = 0;
  percentageChange: number = 0;
  difference: number = 0;
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;

  chartData: MultiLineChartDataPoint[] = [];
  readonly chartHeight = 335;
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;

  readonly series: MultiLineChartSeries[] = [
    { key: 'you', color: '#EE2737', label: this.transloco.translate('driverOverview.chart.you'), showDots: false },
    { key: 'globalAverage', color: '#3873C9', label: this.transloco.translate('driverOverview.chart.globalAverage'), showDots: false }
  ];

  readonly legendItems: LegendItem[] = [
    { legend: this.transloco.translate('driverOverview.chart.you'), color: '#EE2737' },
    { legend: this.transloco.translate('driverOverview.chart.globalAverage'), color: '#3873C9' }
  ];

  readonly yAxisFormatter = (value: any): string => {
    return `${Number(value).toLocaleString('default', { maximumFractionDigits: 2 })}%`;
  };

  constructor() {
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async onAccountFilterChange(filter: AccountFilterData): Promise<void> {
    this.accountFilter = filter;
    this.updateFilter();
  }

  async onDateFilterChange(filter: DateIntervalFilterData): Promise<void> {
    this.dateFilter = filter;
    this.updateFilter();
  }

  async updateFilter(): Promise<void> {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      await this.loadData();
    }
  }

  private async loadData(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      this.cancelSubject.next();

      const request: AccountDateFilterRequest = {
        accountIds: this.accountFilter.accountIds ?? [],
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
        interval: this.dateFilter.interval
      };

      const [youResponse, globalAverageResponse, summaryResponse] = await Promise.all([
        this.driverOverviewService.getDriverPerformanceTimeSeries(request, this.cancelSubject),
        this.globalDriverOverviewService.getDriverPerformanceTimeSeries(request, this.cancelSubject),
        this.driverOverviewService.getDriverPerformanceSummary(
          {
            ...request,
            previousTimeFrom: this.dateFilter.previousCycleStartDate!,
            previousTimeTo: this.dateFilter.previousCycleEndDate!
          },
          this.cancelSubject
        )
      ]);

      const youData = youResponse.data ?? [];
      const globalData = globalAverageResponse.data ?? [];
      const summary = summaryResponse.data;

      this.chartData = this.processData(youData, globalData);
      this.calculateYTicks(this.chartData);
      this.totalValue = summary?.currentValue ?? 0;
      this.percentageChange = summary?.percentageDifference ?? 0;
      this.difference = summary?.difference ?? 0;

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
          this.chartData = [];
        }
      });
    }
  }

  private processData(youData: any[], globalData: any[]): MultiLineChartDataPoint[] {
    if ((!youData || youData.length === 0) && (!globalData || globalData.length === 0)) {
      return [];
    }

    const daysDifference = differenceInDays(this.dateFilter.endDate!, this.dateFilter.startDate!);

    const datasets = [
      {
        label: 'you',
        data: youData.map(item => ({
          totalValue: item.totalValue ?? 0,
          timeSeries: new Date(item.timeSeries)
        }))
      },
      {
        label: 'globalAverage',
        data: globalData.map(item => ({
          totalValue: item.totalValue ?? 0,
          timeSeries: new Date(item.timeSeries)
        }))
      }
    ];

    let groupedData: any[];

    if (daysDifference === 0) {
      groupedData = TimeSeriesGroupingHelper.groupByHours(datasets, true);
    } else if (daysDifference <= 31) {
      groupedData = TimeSeriesGroupingHelper.groupByDays(datasets, this.dateFilter.startDate!, this.dateFilter.endDate!);
    } else if (daysDifference > 60) {
      groupedData = TimeSeriesGroupingHelper.groupByMonths(datasets, this.dateFilter.startDate!, this.dateFilter.endDate!);
    } else {
      groupedData = TimeSeriesGroupingHelper.groupByWeeks(datasets, this.dateFilter.startDate!, this.dateFilter.endDate!);
    }

    return groupedData.map(item => ({
      x: item.label,
      you: item.you ?? 0,
      globalAverage: item.globalAverage ?? 0
    }));
  }

  private calculateYTicks(data: MultiLineChartDataPoint[]): void {
    if (!data || data.length === 0) {
      this.yTicks = this.defaultYTicks;
      return;
    }

    const allValues: number[] = [];

    data.forEach(point => {
      this.series.forEach(s => {
        const value = point[s.key];
        if (typeof value === 'number') {
          allValues.push(value);
        }
      });
    });

    if (allValues.length > 0) {
      const maxValue = Math.max(...allValues);
      // For percentage, cap at 100 if values are near 100
      const paddedMax = maxValue > 80 ? 100 : Math.ceil(maxValue * 1.2);
      const tickInterval = paddedMax / 5;

      this.yTicks = [];
      for (let i = 5; i >= 0; i--) {
        this.yTicks.push(Math.round(tickInterval * i));
      }
    }
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return true; // Higher performance is better
  }

  formatValue(): string {
    return `${this.totalValue.toLocaleString('default', { maximumFractionDigits: 2 })}%`;
  }

  formatTooltipValue = (value: number): string => {
    return `${value.toLocaleString('default', { maximumFractionDigits: 2 })}%`;
  };

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      return this.series.some(s => {
        const value = Number(item[s.key]) || 0;
        return value > 0;
      });
    });
  }

  close(): void {
    this.activeModal.close();
  }
}